import type { Ref, ComputedRef, MaybeRefOrGetter} from 'vue';
import { ref, computed, toRef, watch } from 'vue';
import { useFuse } from '@vueuse/integrations/useFuse';
import { debouncedRef } from '@vueuse/core';

type TreeNodeAppearance = 'normal' | 'header';

export type TreeBase = {
    id: string,
    parentId: string | null,
    name: string,
    appearance?: TreeNodeAppearance
    disabled?: boolean | { disableChildren?: boolean }
};

type SearchFn = (searchString: string, treeRoots: Readonly<TreeNode[]>) => SearchFnResult;

type SearchFnResult = string[] | { id: string, sortModifier: number }[];

export type TreeNodeSelectSettings = {
    // A function that returns an array of string ids for the nodes that should be visible after a search
    searchFn?: SearchFn;
    parentSelectsChildren?: boolean;
    showDeselectOption?: boolean;
    deselectOptionTitle?: string;
};

type UseTreeNodeReturn = {
    nodes: Ref<TreeNode[]>;
    createTree: (treeData: TreeBase[], options?: TreeNodeSelectSettings) => void;
    searchString: Ref<string>;
    isNodeVisible: (node: TreeNode) => boolean;
    visibleNodeIds: ComputedRef<Set<string>>;
    noNodesVisible: ComputedRef<boolean>;
};

export type TreeNode = {
    id: string,
    name: string,
    parentId: string | null,
    children: TreeNode[],
    level: number,
    disabled: boolean,
    appearance: TreeNodeAppearance,
    namePath: string[],
    idPath: string[]
};

export function useTreeNode(settings: MaybeRefOrGetter<TreeNodeSelectSettings> = {}): UseTreeNodeReturn {
    const root = ref<TreeNode>({
        id: '',
        name: '',
        parentId: null,
        level: -1,
        children: [],
        disabled: false,
        appearance: 'normal',
        namePath: [],
        idPath: [],
    });
    const nodes: Ref<TreeNode[]> = ref([]);
    const sortedNodes: Ref<TreeNode[]> = ref([]);
    const searchString = ref('');
    const debouncedSearchString = debouncedRef<string>(searchString, 300);
    const _settings = toRef(settings);

    // Default search function searches entire node path and selects all nodes above the matching nodes
    function searchFn(searchString: string, _tree: TreeNode[]): SearchFnResult {
        const { results } = useFuse(searchString, _tree, {
            fuseOptions: {
                keys: ['namePath'],
                threshold: 0.3,
                includeScore: true,
                ignoreLocation: true,
            }
        });
        const visibleIds = new Set<string>();
        const sortModifierMap = new Map<string, number>();
        for (const node of results.value) {
            const score = 1 - (node.score ?? 1); // invert the score so that the best match is highest
            sortModifierMap.set(node.item.id, score);
            for (const id of node.item.idPath) {
                const currentScore = sortModifierMap.get(id) ?? 0;
                sortModifierMap.set(id, Math.max(currentScore, score));
                visibleIds.add(id);
            }
        }
        return [...visibleIds].map(id => ({ id, sortModifier: sortModifierMap.get(id) ?? 0 }));
    }

    const _activeSearchFn = computed(() => _settings.value.searchFn ?? searchFn);

    const searchResult = computed<{ id: string, sortModifier: number }[]>((): { id: string, sortModifier: number }[] => {
        if (debouncedSearchString.value === '') {
            return nodes.value.map(node => ({ id: node.id, sortModifier: 0 }));
        }
        // Search for nodes that contain the search string
        const hits = _activeSearchFn.value(debouncedSearchString.value, nodes.value);
        return hits.map(id => {
            if (typeof id === 'string') return { id, sortModifier: 0 };
            return id;
        });
    });

    watch([(): { id: string, sortModifier: number }[] => searchResult.value, (): TreeNode[] => nodes.value], () => {
        const rootNode = root.value;
        sortNodes(rootNode);
        sortedNodes.value = getFlatTree(rootNode);
    });

    const visibleNodeIds = computed<Set<string>>(() => new Set(searchResult.value.map(id => {
        if (typeof id === 'string') return id;
        return id.id;
    })));

    const noNodesVisible = computed(() => visibleNodeIds.value.size === 0);

    function createTree(treeData: TreeBase[]): void {
        const rootNode = root.value;
        const nodeChildMap = new Map<string, TreeNode[]>();
        const idMap = new Map<string, TreeBase>();
        for (const node of treeData) {
            nodeChildMap.set(node.id, []);
            idMap.set(node.id, node);
        }

        // Add the root node seperatley
        nodeChildMap.set('', []);

        for (const node of treeData) {
            const newNode: TreeNode = {
                id: node.id,
                name: node.name,
                parentId: node.parentId,
                children: [],
                level: 0,
                disabled: false,
                appearance: node.appearance ?? 'normal',
                namePath: [],
                idPath: [],
            };
            if (node.parentId != null) {
                nodeChildMap.get(node.parentId)?.push(newNode);
            } else {
                nodeChildMap.get('')?.push(newNode);
            }
        }

        rootNode.children = getChildren(rootNode, nodeChildMap);

        setNameAndIdPath(rootNode, [], []);

        setDisabled(rootNode, false, idMap);

        // Since the first node is fake set the level to -1 so the first real node gets 0
        setLevel(rootNode, -1);
        sortNodes(rootNode);
        nodes.value = getFlatTree(rootNode);
    }

    function setDisabled(node: TreeNode, disableChildren: boolean, idMap: Map<string, TreeBase>): void {
        const baseNode = idMap.get(node.id);
        if (baseNode === undefined) {
            for (const child of node.children) {
                setDisabled(child, disableChildren, idMap);
            }
            return;
        }

        const disabled = disableChildren || baseNode.disabled === true || typeof baseNode.disabled === 'object';
        disableChildren = disableChildren || (typeof baseNode.disabled === 'object' && baseNode.disabled.disableChildren === true);

        if (disabled) node.disabled = true;
        for (const child of node.children) {
            setDisabled(child, disableChildren, idMap);
        }
    }

    function setNameAndIdPath(node: TreeNode, parentNames: string[], parentIds: string[]): void {
        node.namePath.push(...parentNames, node.name);
        node.idPath.push(...parentIds, node.id);
        for (const child of node.children) {
            setNameAndIdPath(child, [...parentNames, node.name], [...parentIds, node.id]);
        }
    }

    function getFlatTree(node: TreeNode): TreeNode[] {
        const result: TreeNode[] = [];

        for (const child of node.children) {
            result.push(child);
            result.push(...getFlatTree(child));
        }

        return result;
    }

    function getChildren(node: TreeNode, nodeValues: Map<string, TreeNode[]>): TreeNode[] {
        const children = nodeValues.get(node.id) ?? [];

        node.children = children;

        for (const child of children) {
            getChildren(child, nodeValues);
        }

        return children;
    }

    function isNodeVisible(node: TreeNode): boolean {
        return visibleNodeIds.value.has(node.id);
    }

    function setLevel(node: TreeNode, currentLevel: number = 0): void {
        node.level = currentLevel;
        for (const child of node.children) {
            setLevel(child, currentLevel + 1);
        }
    }

    function sortNodes(node: TreeNode): void {
        for (const child of node.children) {
            sortNodes(child);
        }

        node.children.sort((a, b) => {
            const aSort = searchResult.value.find(hit => hit.id === a.id);
            const bSort = searchResult.value.find(hit => hit.id === b.id);
            if (aSort !== undefined && undefined !== bSort) return bSort.sortModifier - aSort.sortModifier;
            if (aSort !== undefined) return 1;
            if (bSort !== undefined) return -1;
            return a.name.localeCompare(b.name);
        });
    }

    return {
        nodes: sortedNodes,
        createTree,
        isNodeVisible,
        visibleNodeIds,
        searchString,
        noNodesVisible
    };
}
